import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { RoleLayout } from './components/layout/RoleLayout';
import { RequireTier } from './components/layout/RequireTier';
import { RequireMdrTier } from './components/layout/RequireMdrTier';

// Auth pages
import { Login } from './pages/auth/Login';
import { SetPassword } from './pages/auth/SetPassword';

// Admin pages
import { AdminDashboard } from './pages/admin/Dashboard';
import { SellerList } from './pages/admin/SellerList';
import { ApiConfig } from './pages/admin/ApiConfig';
import { SyncManager } from './pages/admin/SyncManager';
import { Companies } from './pages/admin/Companies';
import { SellerDetail } from './pages/admin/SellerDetail';
import { AdminMerchantStats } from './pages/admin/MerchantStats';
import { AdminUsers } from './pages/admin/Users';
import { AdminMdr } from './pages/admin/Mdr';

// MDR pages
import { MdrDashboard } from './pages/mdr/Dashboard';
import { MdrMerchants } from './pages/mdr/Merchants';
import { MdrMerchantDetail } from './pages/mdr/MerchantDetail';
import { MdrSubMdrs } from './pages/mdr/SubMdrs';
import { MdrGroups } from './pages/mdr/Groups';
import { MdrGroupDetail } from './pages/mdr/GroupDetail';
import { MdrAssignments } from './pages/mdr/Assignments';
import { MdrPayouts } from './pages/mdr/Payouts';

// Seller pages
import { SellerDashboard } from './pages/seller/Dashboard';
import { SubSellerList } from './pages/seller/SubSellerList';
import { SubSellerCreate } from './pages/seller/SubSellerCreate';
import { SubSellerEdit } from './pages/seller/SubSellerEdit';
import { TransactionStats } from './pages/seller/TransactionStats';
import { PayoutReports } from './pages/seller/PayoutReports';
import { MerchantList } from './pages/seller/MerchantList';
import { MerchantStats } from './pages/seller/MerchantStats';

// Sub-seller pages
import { SubSellerDashboard } from './pages/sub-seller/Dashboard';
import { SubSellerTransactionStats } from './pages/sub-seller/TransactionStats';
import { SubSellerPayoutReports } from './pages/sub-seller/PayoutReports';
import { SubSellerMerchantList } from './pages/sub-seller/MerchantList';
import { SubSellerMerchantStats } from './pages/sub-seller/MerchantStats';

function RedirectToRole() {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={`/${user.role}/dashboard`} replace />;
}

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Auth routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />

            {/* Admin routes — wrapped in CompanyProvider */}
            <Route
              element={
                <CompanyProvider>
                  <RoleLayout requiredRole="admin" />
                </CompanyProvider>
              }
            >
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/sellers" element={<SellerList />} />
              <Route path="/admin/sellers/:sellerId" element={<SellerDetail />} />
              <Route
                path="/admin/sellers/:sellerId/merchants/:merchantId/stats"
                element={<AdminMerchantStats />}
              />
              {/* MDR management — visible to all admin tiers; actions gated in-page */}
              <Route path="/admin/mdr" element={<AdminMdr />} />
              {/* Superadmin-only sections */}
              <Route element={<RequireTier allow={['super']} />}>
                <Route path="/admin/companies" element={<Companies />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/api-config" element={<ApiConfig />} />
                <Route path="/admin/sync" element={<SyncManager />} />
              </Route>
            </Route>

            {/* Seller routes */}
            <Route element={<RoleLayout requiredRole="seller" />}>
              <Route path="/seller/dashboard" element={<SellerDashboard />} />
              <Route path="/seller/sub-sellers" element={<SubSellerList />} />
              <Route path="/seller/sub-sellers/create" element={<SubSellerCreate />} />
              <Route path="/seller/sub-sellers/:subSellerId/edit" element={<SubSellerEdit />} />
              <Route path="/seller/stats" element={<TransactionStats />} />
              <Route path="/seller/merchants" element={<MerchantList />} />
              <Route path="/seller/merchants/:merchantId/stats" element={<MerchantStats />} />
              <Route path="/seller/payouts" element={<PayoutReports />} />
            </Route>

            {/* Sub-seller routes */}
            <Route element={<RoleLayout requiredRole="sub-seller" />}>
              <Route path="/sub-seller/dashboard" element={<SubSellerDashboard />} />
              <Route path="/sub-seller/stats" element={<SubSellerTransactionStats />} />
              <Route path="/sub-seller/merchants" element={<SubSellerMerchantList />} />
              <Route
                path="/sub-seller/merchants/:merchantId/stats"
                element={<SubSellerMerchantStats />}
              />
              <Route path="/sub-seller/payouts" element={<SubSellerPayoutReports />} />
            </Route>

            {/* MDR routes (master + sub) */}
            <Route element={<RoleLayout requiredRole="mdr" />}>
              <Route path="/mdr/dashboard" element={<MdrDashboard />} />
              <Route path="/mdr/merchants" element={<MdrMerchants />} />
              <Route path="/mdr/merchants/:merchantId" element={<MdrMerchantDetail />} />
              <Route path="/mdr/payouts" element={<MdrPayouts />} />
              {/* Master-only sections */}
              <Route element={<RequireMdrTier allow={['master']} />}>
                <Route path="/mdr/sub-mdrs" element={<MdrSubMdrs />} />
                <Route path="/mdr/groups" element={<MdrGroups />} />
                <Route path="/mdr/groups/:groupId" element={<MdrGroupDetail />} />
                <Route path="/mdr/assignments" element={<MdrAssignments />} />
              </Route>
            </Route>

            {/* Default redirect */}
            <Route path="/" element={<RedirectToRole />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
